import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FaFileContract, FaPaperPlane, FaPlus } from 'react-icons/fa';
import { Select } from '../../../components/forms/Select';
import { POLICY_STATUS_ORDER, PRODUCTS, STATUS_META } from '../constants';
import { usePasStore } from '../pasStore';
import type { Policy } from '../types';
import { formatCurrency, formatDate, relativeTime } from '../utils';
import { Button } from '../components/Button';
import { DataTable, type Column } from '../components/DataTable';
import { NewPolicyModal, type NewPolicyPrefill } from '../components/NewPolicyModal';
import { PageHeader } from '../components/primitives';
import { PolicyStatusBadge } from '../components/StatusBadges';

export const PoliciesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const prefill = (location.state as { prefill?: NewPolicyPrefill } | null)?.prefill;
  const policies = usePasStore((s) => s.policies);
  const loading = usePasStore((s) => s.loading);
  const bulkSubmit = usePasStore((s) => s.bulkSubmitToUnderwriting);
  const brokers = usePasStore((s) => s.brokers);
  const carriers = usePasStore((s) => s.carriers);

  const [status, setStatus] = useState<string>(searchParams.get('status') ?? '');
  const [brokerId, setBrokerId] = useState('');
  const [carrierId, setCarrierId] = useState('');
  const [product, setProduct] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [newOpen, setNewOpen] = useState(!!prefill);
  const [submitting, setSubmitting] = useState(false);

  const filtered = useMemo(
    () =>
      policies.filter(
        (p) =>
          (!status || p.status === status) &&
          (!brokerId || p.broker.id === brokerId) &&
          (!carrierId || p.carrier.id === carrierId) &&
          (!product || p.product === product),
      ),
    [policies, status, brokerId, carrierId, product],
  );

  const columns: Column<Policy>[] = [
    {
      key: 'policyNumber',
      header: 'Policy',
      primary: true,
      sortValue: (p) => p.policyNumber,
      accessor: (p) => (
        <div>
          <span>{p.policyNumber}</span>
          <span className="block text-xs font-normal" style={{ color: 'var(--color-text-secondary)' }}>
            {p.insured.name}
          </span>
        </div>
      ),
    },
    { key: 'product', header: 'Product', sortValue: (p) => p.product, accessor: (p) => <span className="text-sm">{p.product}</span> },
    { key: 'status', header: 'Status', sortValue: (p) => STATUS_META[p.status].label, accessor: (p) => <PolicyStatusBadge status={p.status} /> },
    // Broker column hidden from the table — the broker filter and free-text search still use it.
    { key: 'effectiveDate', header: 'Effective', sortValue: (p) => p.effectiveDate, accessor: (p) => <span className="text-sm">{formatDate(p.effectiveDate)}</span> },
    { key: 'premium', header: 'Annual Premium', align: 'right', sortValue: (p) => p.annualPremium, accessor: (p) => <span className="text-sm font-semibold">{formatCurrency(p.annualPremium)}</span> },
    { key: 'updated', header: 'Updated', align: 'right', sortValue: (p) => p.updatedAt, accessor: (p) => <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{relativeTime(p.updatedAt)}</span> },
  ];

  const statusOptions = [{ value: '', label: 'All statuses' }, ...POLICY_STATUS_ORDER.map((s) => ({ value: s, label: STATUS_META[s].label }))];
  const hasFilters = Boolean(status || brokerId || carrierId || product);

  const handleBulkSubmit = async (ids: string[]) => {
    setSubmitting(true);
    try {
      await bulkSubmit(ids);
      setSelectedIds([]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Policies"
        subtitle={`${filtered.length} of ${policies.length} policies`}
        actions={
          <Button leftIcon={<FaPlus className="h-3 w-3" />} onClick={() => setNewOpen(true)}>
            New Policy
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={filtered}
        getRowId={(p) => p.id}
        onRowClick={(p) => navigate(`/policy-admin/policies/${p.id}`)}
        loading={loading}
        searchable
        searchPlaceholder="Search policy #, insured, broker…"
        searchText={(p) => `${p.policyNumber} ${p.insured.name} ${p.broker.agency} ${p.product}`}
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        initialSort={{ key: 'updated', direction: 'desc' }}
        emptyIcon={<FaFileContract />}
        emptyTitle="No policies match these filters"
        emptyMessage="Try clearing filters or create a policy from an approved quote."
        bulkActions={(ids) => {
          const eligible = filtered.filter((p) => ids.includes(p.id) && p.status === 'draft').map((p) => p.id);
          return (
            <Button
              size="sm"
              leftIcon={<FaPaperPlane className="h-3 w-3" />}
              disabled={eligible.length === 0}
              loading={submitting}
              onClick={() => handleBulkSubmit(eligible)}
            >
              Submit {eligible.length || ''} to Underwriting
            </Button>
          );
        }}
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <Select className="!py-1.5" options={statusOptions} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status" />
            <Select className="!py-1.5" options={[{ value: '', label: 'All brokers' }, ...brokers.map((b) => ({ value: b.id, label: b.agency }))]} value={brokerId} onChange={(e) => setBrokerId(e.target.value)} aria-label="Filter by broker" />
            <Select className="!py-1.5" options={[{ value: '', label: 'All carriers' }, ...carriers.map((c) => ({ value: c.id, label: c.name }))]} value={carrierId} onChange={(e) => setCarrierId(e.target.value)} aria-label="Filter by carrier" />
            <Select className="!py-1.5" options={[{ value: '', label: 'All products' }, ...PRODUCTS.map((p) => ({ value: p, label: p }))]} value={product} onChange={(e) => setProduct(e.target.value)} aria-label="Filter by product" />
            {hasFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStatus('');
                  setBrokerId('');
                  setCarrierId('');
                  setProduct('');
                }}
              >
                Clear
              </Button>
            )}
          </div>
        }
      />

      <NewPolicyModal isOpen={newOpen} onClose={() => setNewOpen(false)} prefill={prefill} />
    </div>
  );
};
